<template>
  <div class="page-default">
    <div class="map-section">
      <div class="dashboard-map">
        <div class="dropdown map-filter">
          <button
            id="dropdownMenuButton"
            type="button"
            class="btn btn-light btn-sm dropdown-toggle"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {{ dateFilter }}
            <font-awesome-icon :icon="['far', 'calendar-alt']" class="ml-3" />
          </button>
          <div
            id="dropdown-menu"
            class="dropdown-menu bg-transparent px-2"
            @click.stop
          >
            <v-date-picker
              class="inline-calendar"
              v-model="dateFilter"
              :model-config="{
                type: 'string',
                mask: 'YYYY-MM-DD',
              }"
              mode="date"
              trim-weeks
            />
          </div>
        </div>
        <div class="map-body">
          <div id="map"></div>
        </div>
        <div class="map-legend" v-if="districtsLegend.length > 0">
          <p class="mb-2 font-weight-medium">Legend</p>
          <div
            class="text-nowrap"
            v-for="district in districtsLegend"
            :key="district.name"
          >
            <span :style="`color: ${district.color}`">
              <font-awesome-icon :icon="['fas', 'circle']" class="mr-2" />
            </span>
            {{ district.name }}
          </div>
        </div>
      </div>
    </div>
    <spinner v-if="isProcessing" />
  </div>
</template>

<script>
import API from "@/api";
import Helper from "@/helper";
import "mapbox-gl";
import Spinner from "@/components/Spinner";
import moment from "moment";
import $ from "jquery";

export default {
  name: "inspection",
  components: {
    spinner: Spinner,
  },
  data() {
    return {
      isProcessing: false,
      mapbox: null,
      map: null,
      dateFilter: moment().format("YYYY-MM-DD"),
      districtsLegend: [],
      mapPopup: null,
    };
  },
  watch: {
    dateFilter: {
      handler() {
        if (this.dateFilter) {
          this.isProcessing = true;

          $(".dropdown-toggle").dropdown("hide");

          this.getDashcamVideos();
        }
      },
      immediate: false,
    },
  },
  methods: {
    async initMap() {
      return new Promise((resolve, reject) => {
        mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_KEY;

        this.map = new mapboxgl.Map({
          container: "map",
          style: "mapbox://styles/mapbox/light-v11",
          center: [101.414161, 3.320954],
          zoom: 9,
        });

        this.map.on("load", () => {
          this.getDashcamVideos();

          resolve();
        });
      });
    },
    async getDashcamVideos() {
      if (this.map) {
        const layers = this.map
          .getStyle()
          .layers.filter((layer) => layer.id.includes("inspection-route-"));

        const sources = Object.keys(
          this.map.getStyle().sources
        ).filter((source) => source.includes("inspection-route-"));

        if (layers && layers.length > 0) {
          layers.forEach((layer) => {
            this.map.removeLayer(layer.id);
          });
        }

        if (sources && sources.length > 0) {
          sources.forEach((source) => {
            this.map.removeSource(source);
          });
        }

        if (this.mapPopup) {
          this.mapPopup.remove();

          this.mapPopup = null;
        }
      }

      const dates = {
        start: moment(this.dateFilter).startOf("day").toISOString(),
        end: moment(this.dateFilter).endOf("day").toISOString(),
      };

      const url = `dashcamvideos?district_null=false&coordinates_null=false&_limit=-1&datetime_gte=${dates.start}&datetime_lte=${dates.end}`;

      return await API.get(url)
        .then((retVal) => {
          const dashcams = retVal.data.filter(
            (data) => data.district && data.district.name != "Test District"
          );

          let districtDashcams = [];

          this.districtsLegend = [];

          dashcams.forEach((dashcam) => {
            let district = districtDashcams.find(
              (dd) => dd._id == dashcam.district._id
            );

            const coordinates = dashcam.coordinates
              .filter((coord) => coord[0] && coord[1])
              .map((coord) => {
                return [coord[1], coord[0]];
              });

            if (coordinates.length > 0) {
              if (!district) {
                districtDashcams.push({
                  _id: dashcam.district._id,
                  district: dashcam.district.name,
                  coordinates: [coordinates],
                });
              } else {
                district.coordinates.push(coordinates);
              }
            }
          });

          districtDashcams.forEach((district, index) => {
            this.districtsLegend.push({
              name: district.district,
              color: Helper.colorPalettes[index],
            });
          });

          if (districtDashcams.length > 0) {
            let bounds = new mapboxgl.LngLatBounds();

            districtDashcams
              .map((district) => district.coordinates)
              .flat()
              .flat()
              .forEach((coord) => {
                bounds.extend([coord[0], coord[1]]);
              });

            this.map.fitBounds(bounds, {
              padding: 25,
              easing: (t) => {
                return t;
              },
            });
          }

          dashcams.forEach((dashcam, index) => {
            this.map.addSource(`inspection-route-${index}`, {
              type: "geojson",
              data: {
                type: "Feature",
                properties: {
                  videoUrl: dashcam.processedVideo
                    ? dashcam.processedVideo
                    : dashcam.video.url,
                },
                geometry: {
                  type: "LineString",
                  coordinates: dashcam.coordinates.map((c) => [c[1], c[0]]),
                },
              },
            });

            const districtIndex = districtDashcams.findIndex(
              (dd) => dd._id === dashcam.district.id
            );

            this.map.addLayer({
              id: `inspection-route-${index}`,
              source: `inspection-route-${index}`,
              type: "line",
              layout: {
                "line-join": "round",
                "line-cap": "round",
              },
              paint: {
                "line-color": Helper.colorPalettes[districtIndex],
                "line-width": 4,
              },
            });

            this.map.on("mouseenter", `inspection-route-${index}`, () => {
              this.map.getCanvas().style.cursor = "pointer";
            });

            this.map.on("mouseleave", `inspection-route-${index}`, () => {
              this.map.getCanvas().style.cursor = "";
            });

            this.map.on("click", `inspection-route-${index}`, (e) => {
              const props = e.features[0].properties;

              const html = `
								<div class="video-popup">
									<video src="${props.videoUrl}" controls muted autoplay />
								</div>
							`;
              this.mapPopup = new mapboxgl.Popup({
                maxWidth: "500px",
                closeButton: false,
                closeOnClick: true,
              })
                .setLngLat(e.lngLat)
                .setHTML(html)
                .addTo(this.map);
            });
          });

          this.isProcessing = false;
        })
        .catch((err) => {
          console.error(err);

          this.isProcessing = false;
        });
    },
  },
  async mounted() {
    this.isProcessing = true;

    await this.initMap();
  },
};
</script>
